import { useMemo } from 'react'
import { Info } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field, Input, Select } from '@/components/ui/Field'
import { RepeatableList } from './RepeatableList'
import { CADENCES } from '@/lib/constants'
import type { CrawlCadence } from '@/types'

export interface CrawlConfigValue {
  excludePatterns: string[]
  /** Null when the client has no crawl config yet. A crawl cannot start until it is set. */
  pageLimit: number | null
  cadence: CrawlCadence | null
}

/**
 * The crawl sends each keyword to Firecrawl as `.*<keyword>.*` against the
 * URL path (APP-54), so a page is excluded when its path contains one.
 */
export function countExcluded(urls: string[], keywords: string[]): number {
  const active = keywords.map((k) => k.trim()).filter(Boolean)
  if (active.length === 0) return 0
  return urls.filter((url) => {
    let path: string
    try {
      path = new URL(url).pathname
    } catch {
      path = url
    }
    return active.some((k) => path.includes(k))
  }).length
}

/**
 * Relocated here from the deleted New Client screen (docs/03-page-specs.md §3).
 *
 * Only the settings the crawl reads are shown. Include patterns and depth
 * limit are stored but not sent to Firecrawl (APP-54), so editing them here
 * would change nothing.
 */
export function CrawlConfigCard({
  value,
  onChange,
  crawledUrls,
}: {
  value: CrawlConfigValue
  onChange: (next: Partial<CrawlConfigValue>) => void
  /** URLs from the last run, used only for the live match preview. */
  crawledUrls: string[]
}) {
  const excluded = useMemo(
    () => countExcluded(crawledUrls, value.excludePatterns),
    [crawledUrls, value.excludePatterns],
  )

  return (
    <Card>
      <CardHeader
        title="Crawl configuration"
        description="Applies to the next run. Saving this does not start a crawl."
      />
      <CardBody className="space-y-5">
        <RepeatableList
          label="Exclude keywords"
          values={value.excludePatterns}
          onChange={(excludePatterns) => onChange({ excludePatterns })}
          placeholder="archive"
          addLabel="Add keyword"
          mono
          hint="A URL whose path contains any of these is not crawled. Plain text, not a glob or a regex."
        />

        {crawledUrls.length > 0 && (
          <p className="flex items-start gap-2 rounded-[4px] bg-brand-blue-50 px-3 py-2 text-[13px] leading-5 text-brand-blue-800">
            <Info size={14} className="mt-0.5 shrink-0" aria-hidden />
            <span>
              These keywords exclude{' '}
              <strong className="tabular font-head font-semibold">{excluded}</strong> of{' '}
              <strong className="tabular font-head font-semibold">{crawledUrls.length}</strong>{' '}
              pages from the last crawl.
            </span>
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Page limit"
            required
            hint={value.pageLimit === null ? 'Required before a crawl can start.' : 'Most pages one crawl may fetch.'}
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={1}
                step={1}
                value={value.pageLimit ?? ''}
                className="tabular"
                onChange={(e) => {
                  const n = Number(e.target.value)
                  onChange({ pageLimit: e.target.value && Number.isInteger(n) && n >= 1 ? n : null })
                }}
              />
            )}
          </Field>

          <Field label="Re-crawl cadence">
            {(p) => (
              <Select
                {...p}
                value={value.cadence ?? ''}
                onChange={(e) => onChange({ cadence: e.target.value as CrawlCadence })}
              >
                {value.cadence === null && (
                  <option value="" disabled>
                    Not set
                  </option>
                )}
                {CADENCES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </CardBody>
    </Card>
  )
}
